import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { EnvDescriptor, EnvLoader } from 'app/Environment/EnvLoader';
import { BehaviorSubject } from 'rxjs';
import { RestaurantHistoryManager } from 'app/RestaurantHistoryManager/RestaurantHistoryManager';
import { CookieHistoryImpl } from 'app/RestaurantHistoryManager/CookieHistoryImpl';
import { Title } from '@angular/platform-browser';
import { UserChildren } from './auth.service';
import {
  Enterprise,
  EnterpriseResponse,
  FullAdmissionsData,
} from './gestion.service';

export interface AddRatingBody {
  rst_id: number;
  crs_id: number;
  rating: number;
  email: string;
  svc_id: number;
}

export interface AddCommentBody {
  rst_id: number;
  category: number;
  comment: string;
  email: string;
  cst_id: number;
  svc_ID: number;
  crs_id: number;
  categorie: 2;
}

export interface PerfoIndicator {
  id: number;
  name: string;
  description: string;
  color: string;
  month: string;
  value: number;
}

export interface Restaurant {
  id: number;
  name: string;
  phone: string;
  email: string;
  address: string;
  postal_code: number;
  city: string;
  latitude: number;
  longitude: number;
  logo: string;
  cstID: number;
  is_use_password: boolean;
  code: string | undefined;
  id_gpao: number;
  sub: any[];
  services: any[];
}

export interface ServiceItem {
  components: RankItem[];
  day_end: string;
  day_start: string;
  id: number;
  label: string;
  name: string;
  rst_id: number;
  svc_id: number;
  svt_id: number;
  time_end: number;
  time_start: number;
  wee_id: number;
  mealtime_gpao_id: number | null;
  mealtime_gpao_name: string | null;
  mealtime_gpao_sejour: string | null;
  mealtime_id: number | null;
  mealtime_ipp: string | null;
  mealtime_name: string | null;
}

export interface RankItem {
  rank: string;
  id: number;
  name: string;
  family_id: number | null;
  courses: CourseItem[];
}

export interface Ingredient {
  id: string;
  name: string;
  technical_sheet: string;
  internal_ref: string;
}

export interface CourseItem {
  id: number;
  gpaoId: string | null;
  name: string;
  is_extra: boolean;
  isMeatFullCourse: boolean;
  isVeganFullCourse: boolean;
  description: string;
  image: string;
  allergens: any[];
  labels: any[];
  bio: number;
  aoc: number;
  local: number;
  local_detail: string;
  label_rouge: number;
  price: number | undefined;
  ingredients: Ingredient[] | undefined;
  vat: number | undefined;
}

export interface Customer {
  customer_id: number;
  name: string;
  use_code: boolean;
  webAppLink: string;
  themeJson: string;
  APP_NAME: string;
}

export interface Allergen {
  name: string;
  logo: string;
  correspondance: string;
}

export interface FullRestaurantData {
  id: number;
  name: string;
  phone: string;
  email: string;
  address: string;
  postal_code: number;
  city: string;
  latitude: number;
  longitude: string;
  payment_url: string;
  pub_past: number;
  pub_future: number;
  logo: string;
  code: string;
  cst_id: number;
  half_boarder: number;
  staff: number;
  is_use_password: boolean;
  services: {
    id: number;
    name: string;
    time_start: string;
    time_end: string;
  }[];
  plan: {
    id: number;
    excl: string[];
  };
  red_periods: any[];
  news: any[];
  perfo: PerfoIndicator[];
  faqs: Faq[];
  events: any[];
  message_footer: string;
  allergens: string[];
  push_notifications: any[];
  pavillons: any[];
}

export interface Faq {
  id: number;
  question: string;
  answer: string;
  date: string;
  order: number;
}

@Injectable({ providedIn: 'root' })
export class QdmService {
  private httpClient = inject(HttpClient);
  private restManager = inject(RestaurantHistoryManager);
  private title = inject(Title);

  env: EnvDescriptor;
  public currentServiceSubject = new BehaviorSubject<ServiceItem | undefined>(
    undefined
  );
  public currentService$ = this.currentServiceSubject.asObservable();
  public currentCustomerSubject = new BehaviorSubject<Customer | null>(null);
  public currentCustomer$ = this.currentCustomerSubject.asObservable();
  public allergensSubject = new BehaviorSubject<Allergen[]>([]);
  public allergens$ = this.allergensSubject.asObservable();
  public selectedDateSubject = new BehaviorSubject<Date | undefined>(undefined);
  public selectedDate$ = this.selectedDateSubject.asObservable();
  public selectedChildSubject = new BehaviorSubject<UserChildren | null>(null);
  public selectChild$ = this.selectedChildSubject.asObservable();
  public restaurantsSubject = new BehaviorSubject<Restaurant[]>([]);
  public restaurants$ = this.restaurantsSubject.asObservable();
  private restaurantsHistorySubject = new BehaviorSubject<Restaurant[]>([]);
  public restaurantsHistory$ = this.restaurantsHistorySubject.asObservable();
  public selectedRestaurantSubject = new BehaviorSubject<
    Restaurant | undefined
  >(undefined);
  public selectedRestaurant$ = this.selectedRestaurantSubject.asObservable();
  private menusSubject = new BehaviorSubject<{ [key: string]: ServiceItem[] }>(
    {}
  );
  public menus$ = this.menusSubject.asObservable();
  public selectedAdmissionSubject =
    new BehaviorSubject<FullAdmissionsData | null>(null);
  public selectedAdmission$ = this.selectedAdmissionSubject.asObservable();

  public groupOrderQuantitySubject = new BehaviorSubject<number>(1);
  public groupOrderQuantity$ = this.groupOrderQuantitySubject.asObservable();

  public fixedCartShowingSubject = new BehaviorSubject<boolean>(false);
  public fixedCartShowing$ = this.fixedCartShowingSubject.asObservable();

  public selectedEnterpriseSubject = new BehaviorSubject<
    Enterprise | undefined
  >(undefined);
  public selectedEnterprise$ = this.selectedEnterpriseSubject.asObservable();

  public preferencesSubject = new BehaviorSubject<any>({});
  public preferences$ = this.preferencesSubject.asObservable();

  /**
   * Vues signal (lecture seule) des subjects ci-dessus.
   * Les écritures passent toujours par les subjects (`.next()`), ce qui garde
   * la compatibilité avec les composants abonnés aux observables.
   */
  readonly currentService = toSignal(this.currentService$, { requireSync: true });
  readonly currentCustomer = toSignal(this.currentCustomer$, { requireSync: true });
  readonly selectedDate = toSignal(this.selectedDate$, { requireSync: true });
  readonly selectedChild = toSignal(this.selectChild$, { requireSync: true });
  readonly selectedRestaurant = toSignal(this.selectedRestaurant$, { requireSync: true });
  readonly selectedAdmission = toSignal(this.selectedAdmission$, { requireSync: true });
  readonly groupOrderQuantity = toSignal(this.groupOrderQuantity$, { requireSync: true });
  readonly selectedEnterprise = toSignal(this.selectedEnterprise$, { requireSync: true });
  readonly preferences = toSignal(this.preferences$, { requireSync: true });

  constructor() {
    let env: EnvDescriptor;

    if (!(globalThis as any).env) {
      env = EnvLoader.load();
      (globalThis as any).env = env;
    } else {
      env = (globalThis as any).env;
    }

    this.env = env;
  }

  /**
   * Affiche dans la console les valeurs courantes des sujets principaux (environnement, restaurants, menus, etc.).
   */
  printAll() {
    let data = {
      env: JSON.stringify(this.env),
      restaurants: JSON.stringify(this.restaurantsSubject.value),
      restaurantsHistory: JSON.stringify(this.restaurantsHistorySubject.value),
      selectedRestaurant: JSON.stringify(this.selectedRestaurantSubject.value),
      menus: JSON.stringify(this.menusSubject.value),
      currentService: JSON.stringify(this.currentServiceSubject.value),
      currentCustomer: JSON.stringify(this.currentCustomerSubject.value),
      allergens: JSON.stringify(this.allergensSubject.value),
    };
    console.table(data, [
      'env',
      'restaurants',
      'restaurantsHistory',
      'selectedRestaurant',
      'menus',
      'currentService',
      'currentCustomer',
      'allergens',
    ]);
  }

  /**
   * Définit l'entreprise sélectionnée dans le sujet correspondant.
   * @param e Entreprise sélectionnée ou undefined.
   */
  selectEnterprise(e: Enterprise | undefined) {
    this.selectedEnterpriseSubject.next(e);
  }

  /**
   * Récupère la liste des restaurants selon le domaine courant (public ou privé).
   */
  async listRestaurantsForCurrentDomain(): Promise<void> {
    return new Promise((resolve) => {
      if (this.restaurantsSubject.value.length === 0) {
        const subdomain = window.location.hostname
          .split('.')[0]
          .toLocaleLowerCase();
        if (this.isPublicDomainOrPrivate() === 'public') {
          this.#get(`${this.#url()}/restaurants?all=light`).then(
            (restaurants) => {
              if (this.restaurantsSubject.value !== restaurants.restaurants) {
                this.restaurantsSubject.next(restaurants.restaurants);
                resolve();
              }
            }
          );
        } else {
          this.listRestaurantsOfDomain(subdomain).then((restaurants) => {
            this.restaurantsSubject.next(restaurants);
            resolve();
          });
        }
      }
    });
  }

  /**
   * Récupère le conseil du jour (tip of the day) pour un client donné.
   * @param cstId ID du client (optionnel).
   * @returns Une promesse résolue avec un tableau de conseils.
   */
  async getTipOfTheDay(cstId: number | undefined = undefined): Promise<
    {
      type: string;
      auteur: string;
      texte: string;
    }[]
  > {
    return new Promise((resolve, reject) => {
      this.#get(
        `${this.#url()}/tipoftheday${
          cstId !== undefined ? `?cst_id=${cstId}` : ''
        }`
      ).then((res) => {
        if (res.success) {
          resolve(res.tip);
        } else {
          reject();
        }
      });
    });
  }

  private restaurantCache: Map<number, FullRestaurantData> = new Map();

  /**
   * Récupère un restaurant avec toutes ses données à partir de son ID.
   * Utilise un cache interne pour éviter les appels redondants.
   * @param id ID du restaurant.
   * @returns Une promesse résolue avec les données du restaurant.
   */
  fetchRestaurantById(id: number): Promise<FullRestaurantData> {
    if (this.restaurantCache.has(id)) {
      return Promise.resolve(this.restaurantCache.get(id)!);
    }

    return new Promise((resolve, reject) => {
      this.#get(`${this.#url()}/restaurants/${id}?all=light`).then((res) => {
        if (res.success) {
          this.restaurantCache.set(id, res.restaurant);
          resolve(res.restaurant);
        } else {
          reject();
        }
      });
    });
  }

  
  /**
   * Sélectionne un restaurant à partir de son ID.
   * Met à jour les sujets correspondants et effectue une requête si nécessaire.
   * @param id ID du restaurant à sélectionner.
   */
  selectRestaurantById(id: number) {
    let foundRestaurant = undefined;
    this.restaurantsSubject.value.forEach((restaurant) => {
      if (restaurant.id === id) {
        foundRestaurant = restaurant;
      }
    });

    if (foundRestaurant) {
      if (
        this.selectedRestaurantSubject.value &&
        this.selectedRestaurantSubject.value.id === (foundRestaurant as any).id
      ) {
        return;
      }

      this.selectedRestaurantSubject.next(foundRestaurant);
      return;
    }

    this.#get(`${this.#url()}/restaurants/${id}`).then((res) => {
      if (res.success) {
        const restauResponse = res.restaurant as Restaurant;
        if (
          restauResponse.id === (this.selectedRestaurantSubject.value as any).id
        ) {
          return;
        }

        this.selectedRestaurantSubject.next(restauResponse);
        this.selectedAdmissionSubject.next(null);
      }
    });
  }

  /**
   * Charge l'historique des restaurants visités à partir des cookies.
   */
  loadRestaurantsHistory() {
    this.restaurantsHistorySubject.next(
      this.restManager.getRestaurant(new CookieHistoryImpl())
    );
  }

    /**
   * Définit le restaurant sélectionné et met à jour l'historique.
   * Réinitialise les allergènes si aucun restaurant n'est sélectionné.
   * Réinitialise toutes les données liées au restaurant précédent pour éviter les conflits.
   * @param restaurant Restaurant sélectionné ou undefined.
   */
  selectRestaurant(restaurant: Restaurant | undefined) {
    this.restaurantsHistorySubject.next(
      this.restManager.getRestaurant(new CookieHistoryImpl())
    );

    if (!restaurant && !this.selectedRestaurantSubject.value) {
      return;
    }

    if (
      restaurant &&
      this.selectedRestaurantSubject.value &&
      restaurant.id === this.selectedRestaurantSubject.value.id
    ) {
      return;
    }

    // Réinitialiser toutes les données liées au restaurant précédent
    // pour éviter les conflits lors du changement de restaurant
    this.currentServiceSubject.next(undefined);
    this.selectedDateSubject.next(undefined);
    this.selectedAdmissionSubject.next(null);
    this.selectedEnterpriseSubject.next(undefined);
    this.allergensSubject.next([]);
    
    // Vider le cache des menus pour éviter les conflits
    this.menusSubject.next({});

    this.selectedRestaurantSubject.next(restaurant);

    if (restaurant) {
      this.restManager.addRestaurant(new CookieHistoryImpl(), restaurant);
      this.title.setTitle(`${this.env.title} - ${restaurant.name}`);
    }

    this.restaurantsHistorySubject.next(
      this.restManager.getRestaurant(new CookieHistoryImpl())
    );
  }

  /**
   * Récupère les menus d'un restaurant pour un client donné.
   * Met en cache le résultat dans un sujet BehaviorSubject.
   * @param cstId ID du client.
   * @param id ID du restaurant.
   * @returns Une promesse résolue avec les services (menus).
   */
  async fetchMenus(cstId: number, id: number) {
    let oldValue = this.menusSubject.value;
    if (!oldValue[`${cstId};${id}`]) {
      const response = await this.#get(
        `${this.#url()}/menus/${id}?wso_id=1&cst_id=${cstId}`
      );
      if (!response.success) {
        return [];
      }

      oldValue[`${cstId};${id}`] = response.menus;
      this.menusSubject.next(oldValue);
    }
    return oldValue[`${cstId};${id}`];
  }

    /**
   * Récupère tous les restaurants associés à un sous-domaine spécifique.
   * @param subdomain Sous-domaine du client.
   * @returns Une promesse résolue avec les restaurants.
   */
  async listRestaurantsOfDomain(subdomain: string): Promise<Restaurant[]> {
    const customers = await this.fetchAllCustomers();

    let foundCustomer: Customer | undefined;

    customers.forEach((customer) => {
      if (foundCustomer) return;

      let customer_id: number = customer.customer_id;
      let name: string = customer.name;
      let use_code: boolean = customer.USE_CODE === '1';
      let webAppLink: string = customer.WEBAPP_LINK;

      if (!webAppLink || webAppLink === '') return;

      webAppLink = webAppLink.split('.')[0];

      let themeJson: string = customer.THEME_JSON;

      if (!themeJson || themeJson === '') themeJson = '{}';

      if (subdomain.toLowerCase() === webAppLink.toLocaleLowerCase()) {
        foundCustomer = {
          customer_id: customer_id,
          name: name,
          use_code: use_code,
          webAppLink: webAppLink,
          themeJson: themeJson,
          APP_NAME: customer.APP_NAME,
        };
      }
    });

    if (!foundCustomer) {
      return [];
    }

    this.currentCustomerSubject.next(foundCustomer);

    const cstId = foundCustomer.customer_id;
    return await this.#fetchAllRestaurantsFromCstId(cstId);
  }

  
  /**
   * Récupère la liste complète des clients (customers).
   * @returns Une promesse résolue avec un tableau de clients.
   */
  async fetchAllCustomers(): Promise<any[]> {
    return new Promise((res, rej) => {
      this.#get(`${this.#url()}/customer`).then((response) => {
        if (response.success) {
          res(response.customers);
        } else {
          rej();
        }
      });
    });
  }

  /**
   * Détermine si l'application doit fonctionner en mode public ou privé
   * @returns "public" si l'application est en mode public, "private" si l'application est en mode privé
   */
  isPublicDomainOrPrivate(): 'public' | 'private' {
    const subdomain = window.location.hostname
      .split('.')[0]
      .toLocaleLowerCase();
    const href = window.location.href;

    // L'utilisation de ngrok, ou de certains sous-domaines, indique que l'application est en mode public
    if (
      ['validapp', 'app', 'pyr-pc-ryh', 'mac-ryan'].includes(subdomain) ||
      href.includes('ngrok')
    )
      return 'public';
    else return 'private';
  }

  /**
   * Récupère la liste des allergènes d'un restaurant donné.
   * @param rstId ID du restaurant.
   * @returns Une promesse résolue avec les allergènes.
   */
  fetchAllergens(rstId: number): Promise<Allergen[]> {
    return new Promise((res) => {
      this.#get(`${this.#url()}/restaurants/allergens?rst_id=${rstId}`).then(
        (response) => {
          if (response.allergens) {
            res(response.allergens);
          }
        }
      );
    });
  }

  #url(): string {
    return `${this.env.qdmApiUrl}/rest`;
  }

  #get(url: string): Promise<any> {
    if (!url) {
      console.error('URL est indéfinie ou vide');
      return Promise.reject(new Error("L'URL est manquante ou incorrecte"));
    }

    return new Promise((res, rej) => {
      this.httpClient
        .get<any>(url, { headers: { Accept: 'application/json' } })
        .subscribe((data) => {
          res(data);
        });
    });
  }

  /**
   * Récupère la liste des restaurants associés à un client donné (interne).
   * @param cstId ID du client.
   * @returns Une promesse résolue avec les restaurants.
   */
  async #fetchAllRestaurantsFromCstId(cstId: number): Promise<Restaurant[]> {
    return new Promise((res, rej) => {
      this.#get(`${this.#url()}/restaurants?cst_id=${cstId}`).then(
        (response) => {
          if (response.success) {
            res(response.restaurants);
          } else {
            rej();
          }
        }
      );
    });
  }

   /**
   * Effectue une requête HTTP GET générique avec entête JSON.
   * @param url URL à interroger.
   * @returns Une promesse résolue avec la réponse.
   */
  fetchRestaurantsWithCode(
    inputCode: string,
    cstId: number
  ): Promise<Restaurant[]> {
    return new Promise((res) => {
      fetch(`${this.#url()}/restaurants?code=${inputCode}&cst_id=${cstId}`)
        .then((r) => r.json())
        .then((response: any) => {
          res((response.restaurants as Restaurant[]) || []);
        })
        .catch((err) => {
          console.error(err);
          res([]);
        });
    });
  }

  /**
   * Ajoute une note à un plat.
   * @param body Corps de la requête contenant les données nécessaires.
   * @returns Une promesse résolue avec un booléen indiquant si l'opération a été réussie.
   */
  addRating(body: AddRatingBody): Promise<boolean> {
    return new Promise<boolean>((res) => {
      fetch(`${this.#url()}/courses/addrating`, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: JSON.stringify(body),
      })
        .then((r) => r.json())
        .then((r) => {
          res((r.success as boolean) || true);
        })
        .catch(() => res(false));
    });
  }

  /**
   * Ajoute un commentaire à un plat.
   * @param body Corps de la requête contenant les données nécessaires.
   * @returns Une promesse résolue avec un booléen indiquant si l'opération a été réussie.
   */
  addComment(body: AddCommentBody): Promise<boolean> {
    return new Promise((res) => {
      fetch(`${this.#url()}/restaurants/addcomment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: JSON.stringify(body),
      })
        .then((r) => r.json())
        .then((r) => {
          res((r.success as boolean) || true);
        })
        .catch(() => res(false));
    });
  }

  /**
   * Reset all subjects to default values
   */
  deleteAll() {
    this.currentServiceSubject.next(undefined);
    this.currentCustomerSubject.next(null);
    this.allergensSubject.next([]);
    this.selectedDateSubject.next(new Date());
    this.selectedChildSubject.next(null);
    this.restaurantsSubject.next([]);
    this.restaurantsHistorySubject.next([]);
    this.selectedRestaurantSubject.next(undefined);
    this.menusSubject.next({});
    this.selectedAdmissionSubject.next(null);
    this.groupOrderQuantitySubject.next(1);
  }
}
